import React, { useEffect, useState } from 'react';
import { Row, Col, Button, Card, Typography, Rate, Spin } from 'antd';
import {
  ShoppingCartOutlined,
  HeartOutlined,
  SafetyCertificateOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PublicWebContentService from '../services/publicWebContentService';
import { productService, storageUrl } from '../services/api';
import './Home.css';

const { Title, Paragraph } = Typography;

const PLACEHOLDER = 'https://placehold.co/600x450/eef2f7/94a3b8?text=Current+Care';

const rs = (n) => 'Rs ' + Number(n || 0).toLocaleString('en-IN');
const marketPrice = (p) =>
  p.sale_price != null && Number(p.sale_price) < Number(p.price) ? Number(p.price) : null;

const AVATAR_TINTS = ['#e9f3ff', '#fff4e2', '#eafaf0', '#f3ecff', '#fdeef0'];
const AVATAR_INK = ['#0363d9', '#b35c00', '#16a34a', '#7c3aed', '#e11d48'];
const initials = (name) =>
  (name || 'A')
    .split(' ')
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

const Home = () => {
  const [heroHeadline, setHeroHeadline] = useState(
    'Your trusted partner for electrical appliances, electronics, and expert repair services.',
  );
  const [heroSubheadline, setHeroSubheadline] = useState(
    'Shop quality electrical products, book professional installation and repair services, and keep your home powered with reliable solutions.',
  );
  const [heroStats, setHeroStats] = useState([
    { number: '10K+', label: 'Customers' },
    { number: '98%', label: 'Satisfaction' },
    { number: '50+', label: 'Services' },
  ]);
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  const requireAuth = (arg) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      if (typeof arg === 'function') arg();
      else navigate(arg);
    } else {
      const path = typeof arg === 'function' ? window.location.pathname : arg;
      navigate('/login', { state: { from: path } });
    }
  };

  const sectionToMap = (items) => {
    const map = {};
    (items || []).forEach((item) => {
      map[item.content_key] = item.content_value;
    });
    return map;
  };

  useEffect(() => {
    const loadAll = async () => {
      try {
        const apiBase = process.env.REACT_APP_API_URL || '/api';
        const [pageContent, productsRes, reviewsRes, categoriesRes] = await Promise.all([
          PublicWebContentService.getPageContent('home').catch(() => null),
          productService.getProducts({ limit: 8, type: 'product' }).catch(() => null),
          fetch(`${apiBase}/testimonials`)
            .then((r) => (r.ok ? r.json() : { data: [] }))
            .catch(() => ({ data: [] })),
          productService.getCategories().catch(() => null),
        ]);

        if (pageContent) {
          const hero = sectionToMap(pageContent.hero);
          const stats = sectionToMap(pageContent.stats);
          if (hero.headline) setHeroHeadline(hero.headline);
          if (hero.subheadline) setHeroSubheadline(hero.subheadline);
          const newStats = [];
          if (stats.customers_count)
            newStats.push({
              number:
                Number(stats.customers_count) >= 1000
                  ? `${(Number(stats.customers_count) / 1000).toFixed(0)}K+`
                  : `${stats.customers_count}+`,
              label: 'Customers',
            });
          if (stats.satisfaction_rate)
            newStats.push({ number: `${stats.satisfaction_rate}%`, label: 'Satisfaction' });
          if (stats.services_count)
            newStats.push({ number: `${stats.services_count}+`, label: 'Services' });
          if (newStats.length >= 2) setHeroStats(newStats);
        }

        const productsData = productsRes?.data?.data || productsRes?.data || [];
        if (Array.isArray(productsData)) {
          setProducts(
            productsData.map((p) => ({
              ...p,
              image: storageUrl(p.images?.[0]?.url || p.images?.[0]?.image_url) || PLACEHOLDER,
              brand: p.brand || p.category || '',
              desc: p.description || p.short_description || '',
              categorySlug: (p.category || '').toLowerCase().replace(/\s+/g, '-'),
              rating: Number(p.average_rating) || p.rating || 0,
            })),
          );
        }

        const reviewsData = reviewsRes?.data || [];
        if (Array.isArray(reviewsData) && reviewsData.length > 0) {
          setReviews(reviewsData.slice(0, 4));
        }

        const catsData = categoriesRes?.data?.data || categoriesRes?.data || [];
        if (Array.isArray(catsData)) {
          setCategories(catsData.slice(0, 4));
        }
      } catch (error) {
        console.error('Failed to load home data', error);
      } finally {
        setLoading(false);
      }
    };
    loadAll();
  }, []);

  return (
    <div className="home-page">
      <section className="hero">
        <div className="hero-inner">
          <div className="hero-copy">
            <h1 className="hero-headline">{heroHeadline}</h1>
            <p className="hero-text">{heroSubheadline}</p>
            <div className="hero-actions">
              <Button
                type="primary"
                size="large"
                className="cta-primary"
                icon={<ShoppingCartOutlined />}
                onClick={() => navigate('/products')}
              >
                View Products
              </Button>
              <Button
                size="large"
                className="cta-secondary"
                icon={<CalendarOutlined />}
                onClick={() => navigate('/service-catalog')}
              >
                Book a Service
              </Button>
            </div>
            <div className="hero-trust">
              {heroStats.map((stat, index) => (
                <div className="t" key={index}>
                  <b>{stat.number}</b>
                  <span>{stat.label}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="hero-media">
            <img
              className="hero-photo"
              src="/images/hero-technician.jpg"
              alt="Certified Current Care technician servicing electrical equipment"
            />
            <div className="hero-float">
              <span className="icon">
                <SafetyCertificateOutlined />
              </span>
              <div>
                <b>Certified technicians</b>
                <span>Warranty-backed service</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="home-section cat-section">
        <div className="section-header">
          <Title level={2}>Shop by category</Title>
          <Paragraph>
            From appliances and electronics to genuine components and expert repair, everything for
            your home in one place.
          </Paragraph>
        </div>
        <div className="cat-grid">
          {(categories.length > 0
            ? categories.map((cat) => ({
                title: cat.name,
                img: storageUrl(cat.image_url) || `/images/cat-appliances.jpg`,
                to: `/products?category=${encodeURIComponent(cat.name)}`,
              }))
            : [
                {
                  title: 'Appliances',
                  img: '/images/cat-appliances.jpg',
                  to: '/products?category=appliances',
                },
                {
                  title: 'Electronics',
                  img: '/images/cat-electronics.jpg',
                  to: '/products?category=electronics',
                },
                {
                  title: 'Components',
                  img: '/images/cat-components.jpg',
                  to: '/products?category=components',
                },
                {
                  title: 'Repair & Installation',
                  img: '/images/cat-repair.jpg',
                  to: '/service-catalog',
                },
              ]
          ).map((c) => (
            <div
              className="cat-tile"
              key={c.title}
              role="button"
              tabIndex={0}
              onClick={() => navigate(c.to)}
              onKeyDown={(e) => e.key === 'Enter' && navigate(c.to)}
            >
              <img src={c.img} alt={c.title} loading="lazy" />
              <div className="cat-tile-body">
                <h3>{c.title}</h3>
                <span>
                  Browse <ArrowRight size={15} />
                </span>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="home-section featured-grid">
        <div className="section-header">
          <Title level={2}>Trending products</Title>
          <Paragraph>Top-selling appliances and electronics customers across Nepal love.</Paragraph>
        </div>
        <Row gutter={[24, 24]}>
          {(products.length > 0
            ? products
            : [
                {
                  id: 'tv',
                  brand: 'Samsung',
                  name: '55" Crystal 4K UHD Smart TV',
                  desc: 'Crystal 4K · HDR10',
                  price: 89999,
                  discount: 18,
                  image: '/images/products/tv.jpg',
                },
                {
                  id: 'fridge',
                  brand: 'LG',
                  name: 'Double-Door Refrigerator',
                  desc: 'Frost-free · Inverter',
                  price: 74999,
                  discount: 15,
                  image: '/images/products/fridge.jpg',
                },
                {
                  id: 'washer',
                  brand: 'Whirlpool',
                  name: 'Front-Load Washing Machine',
                  desc: '1200 RPM · 7kg',
                  price: 62500,
                  discount: 12,
                  image: '/images/products/washer.jpg',
                },
                {
                  id: 'laptop',
                  brand: 'HP',
                  name: '14" Core i5 Laptop',
                  desc: '512GB SSD · 16GB',
                  price: 82000,
                  discount: 10,
                  image: '/images/products/laptop.jpg',
                },
              ]
          ).map((product) => (
            <Col xs={12} sm={12} md={6} key={product.id}>
              <Card
                hoverable
                className="product-card"
                cover={
                  <div className="product-cover">
                    <img
                      alt={product.name}
                      src={product.image || product.img || PLACEHOLDER}
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = PLACEHOLDER;
                      }}
                    />
                    {product.discount ? (
                      <span className="product-badge">{product.discount}% off</span>
                    ) : null}
                    <button
                      type="button"
                      className="product-wish"
                      aria-label="Add to wishlist"
                      onClick={(e) => {
                        e.stopPropagation();
                        requireAuth('/wishlist');
                      }}
                    >
                      <HeartOutlined />
                    </button>
                  </div>
                }
                onClick={() => navigate('/products')}
              >
                <div className="product-brand">{product.brand}</div>
                <div className="product-title">{product.name}</div>
                <div className="product-desc">{product.desc || product.description}</div>
                <div className="product-price-row">
                  <span className="product-price">{rs(product.sale_price ?? product.price)}</span>
                  {marketPrice(product) ? (
                    <span className="product-original">{rs(marketPrice(product))}</span>
                  ) : null}
                </div>
                <Button
                  type="primary"
                  block
                  className="product-cart-btn"
                  icon={<ShoppingCartOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate('/products');
                  }}
                >
                  Add to Cart
                </Button>
              </Card>
            </Col>
          ))}
        </Row>
        <div className="section-cta">
          <Button size="large" onClick={() => navigate('/products')}>
            View all products <ArrowRight size={16} style={{ marginLeft: 4, verticalAlign: -3 }} />
          </Button>
        </div>
      </section>

      <section className="home-section reviews-section">
        <div className="section-header">
          <Title level={2}>Loved by customers across Nepal</Title>
          <Paragraph>
            Real feedback from people who shop and book repairs with Current Care.
          </Paragraph>
        </div>
        <Row gutter={[24, 24]}>
          {(reviews.length > 0
            ? reviews
            : [
                {
                  id: 1,
                  name: 'Sabin Shrestha',
                  rating: 5,
                  text: 'Ekdam ramro service! Mero TV tehi din delivery bhayo ra install pani free. Dhanyabaad Current Care.',
                },
                {
                  id: 2,
                  name: 'Anjana Gurung',
                  rating: 5,
                  text: 'Washing machine kineko, price sasto ra quality ekdam ramro. Technician pani professional thiyo.',
                },
                {
                  id: 3,
                  name: 'Bikash Thapa',
                  rating: 4,
                  text: 'Fridge bigreko thiyo, same day repair garidiyo. Ekdam bharparne ra bharosa laayak service ho.',
                },
                {
                  id: 4,
                  name: 'Sunita Tamang',
                  rating: 5,
                  text: 'Online order garna sajilo, delivery pani chhito aayo. Nepal kai lagi best electronics store!',
                },
              ]
          ).map((review, index) => (
            <Col xs={24} md={12} lg={6} key={review.id || index}>
              <Card className="testimonial-card" hoverable>
                <div className="review-head">
                  {review.img ? (
                    <img
                      className="review-avatar"
                      src={review.img}
                      alt={review.name}
                      loading="lazy"
                    />
                  ) : (
                    <span
                      className="review-avatar review-avatar--initials"
                      style={{
                        color: AVATAR_INK[index % AVATAR_INK.length],
                        background: AVATAR_TINTS[index % AVATAR_TINTS.length],
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        fontWeight: 600,
                        fontSize: 14,
                      }}
                    >
                      {initials(review.name)}
                    </span>
                  )}
                  <div>
                    <div className="review-name">{review.name}</div>
                    {review.city ? <div className="review-city">{review.city}</div> : null}
                  </div>
                </div>
                <Rate disabled defaultValue={review.rating} style={{ marginBottom: 12 }} />
                <Paragraph className="review-text">{review.text}</Paragraph>
              </Card>
            </Col>
          ))}
        </Row>
      </section>
    </div>
  );
};

export default Home;
